<script setup lang="ts">
  import { useOfferteStore } from "../stores/offertes";
  import { useAuthStore } from "../stores/auth";
  import { useRouter, useRoute } from "vue-router";
  import { ref, onMounted, computed, watch } from "vue";

  // Components
  import AppHeader from "../components/AppHeader.vue";
  import CustomTag from "../components/CustomTag.vue";
  import BundelOverview from "../components/BundelOverview.vue";

  // PrimeVue Components
  import DataView from "primevue/dataview";
  import Panel from "primevue/panel";
  import Select from "primevue/select";
  import ToggleButton from "primevue/togglebutton";
  import IconField from "primevue/iconfield";
  import InputIcon from "primevue/inputicon";
  import InputText from "primevue/inputtext";
  import Button from "primevue/button";
  import Popover from "primevue/popover";
  import Checkbox from "primevue/checkbox";
  import Tabs from "primevue/tabs";
  import TabList from "primevue/tablist";
  import Tab from "primevue/tab";
  import TabPanels from "primevue/tabpanels";
  import TabPanel from "primevue/tabpanel";
  import { Offerte, OfferteStatus } from "../../../../types";
  import { resolveStorageUrl } from "../utils/storageUrl";

  const router = useRouter();
  const route = useRoute();
  const offerteStore = useOfferteStore();
  const authStore = useAuthStore();

  // Tab navigation
  const activeTab = ref("0");

  // Existing reactive data
  const selectedImage = ref<string | null>(null);
  const searchQuery = ref("");

  // New reactive data for enhanced functionality
  const statusFilter = ref("all");
  const showIngetrokken = ref(false);
  const scopeFilters = ref({
    scope8: false,
    scope10: false,
    scope12: false,
  });
  const rows = ref(8);
  const displayCount = ref(rows.value);

  const expandedOffertes = ref<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    const next = new Set(expandedOffertes.value);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    expandedOffertes.value = next;
  };

  // Status history popover
  const statusPopover = ref();
  const hoveredOfferteId = ref<string | null>(null);

  type StatusHistoryEntry = {
    status: OfferteStatus;
    datum: string;
    door?: string | null;
  };

  const hoveredOfferte = computed<Offerte | null>(() => {
    const id = hoveredOfferteId.value;
    if (!id) return null;
    return offerteStore.offertes.find((offerte) => offerte.id === id) ?? null;
  });

  // Filter options
  const statusOptions = [
    { label: "Alle", value: "all" },
    { label: "Nieuw", value: "nieuw" },
    { label: "Deels Geaccepteerd", value: "deels_geaccepteerd" },
    { label: "Geaccepteerd", value: "alles_geaccepteerd" },
    { label: "Foute inzending", value: "foute_inzending" },
    { label: "Ingepland", value: "ingepland" },
    { label: "Ingetrokken", value: "ingetrokken" },
  ];

  // Search is now handled reactively by computed filteredOffertes

  const closeImageModal = () => {
    selectedImage.value = null;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Onbekend";
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch (error) {
      return "Onbekend";
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  // Helper functions for status handling
  const getCurrentStatus = (offerte: Offerte): OfferteStatus => {
    const latestStatus = offerte.status?.[offerte.status.length - 1]?.status;
    return latestStatus ?? "nieuw";
  };

  const isIngetrokken = (offerte: Offerte) => {
    return getCurrentStatus(offerte) === "ingetrokken";
  };

  const getOfferteStatusInfo = (offerte: Offerte): { status: OfferteStatus; color: string } => {
    // Handle ingetrokken status first (takes precedence)
    if (isIngetrokken(offerte)) {
      return { status: "ingetrokken", color: "ingetrokken" };
    }

    // Get the latest status from the status array
    if (offerte.status && Array.isArray(offerte.status) && offerte.status.length > 0) {
      const latestStatus = offerte.status[offerte.status.length - 1]?.status;
      const status = latestStatus ?? "nieuw";
      return { status, color: status };
    }

    // Default to "nieuw" if no status is found
    return { status: "nieuw", color: "nieuw" };
  };

  const getStatusLabel = (status: OfferteStatus) => {
    const labels = {
      nieuw: "Nieuw",
      aangepast: "Nieuw",
      verzonden: "Nieuw",
      bekeken: "Nieuw",
      deels_geaccepteerd: "Deels Geaccepteerd",
      alles_geaccepteerd: "Geaccepteerd",
      foute_inzending: "Foute inzending",
      ingepland: "Ingepland",
      ingetrokken: "Ingetrokken",
      afgewezen: "Afgewezen",
      gefactureerd: "Gefactureerd",
    };
    return labels[status] || status;
  };

  // Helper function to check if a status should be grouped as "Nieuw"
  const isNieuwStatus = (status: OfferteStatus) => {
    return ["nieuw", "aangepast", "verzonden", "bekeken"].includes(status);
  };

  // Helper functions for scope filtering
  const hasProductType = (offerte: Offerte, productType: string) => {
    if (!offerte?.producten) return false;
    return offerte.producten.some((product) => product.productType === productType);
  };

  const hasScope8 = (offerte: Offerte) => {
    return hasProductType(offerte, "scope8pi") || hasProductType(offerte, "scope8ebi");
  };

  const hasScope10 = (offerte: Offerte) => {
    return hasProductType(offerte, "scope10");
  };

  const hasScope12 = (offerte: Offerte) => {
    return hasProductType(offerte, "scope12pi") || hasProductType(offerte, "scope12ebi");
  };

  const hasNen1010 = (offerte: Offerte) => {
    return hasProductType(offerte, "nen1010");
  };

  const hasNen3140 = (offerte: Offerte) => {
    return hasProductType(offerte, "nen3140");
  };

  // Status history helpers
  const getStatusHistory = (offerte?: Offerte | null): StatusHistoryEntry[] => {
    if (!offerte?.status || !Array.isArray(offerte.status)) return [];
    // Return in chronological order (most recent first)
    return (offerte.status as unknown as StatusHistoryEntry[]).slice().reverse();
  };

  const showStatusHistory = (event: Event, offerte: Offerte) => {
    hoveredOfferteId.value = offerte.id;
    statusPopover.value?.toggle(event);
  };

  const handleLogoError = (event: Event) => {
    const target = event.target as HTMLImageElement | null;
    if (target) {
      target.style.display = "none";
    }
  };

  const toOfferteItems = (items: unknown[]): Offerte[] => {
    return items as Offerte[];
  };

  // Computed properties for filtering and pagination
  const filteredOffertes = computed(() => {
    if (!offerteStore.offertes || offerteStore.offertes.length === 0) {
      return [];
    }

    const source = offerteStore.offertes as Offerte[];
    const includeIngetrokken = showIngetrokken.value || statusFilter.value === "ingetrokken";

    const statusFiltered: Offerte[] = [];
    for (const offerte of source) {
      const currentStatus = getCurrentStatus(offerte);

      if (isIngetrokken(offerte)) {
        if (!includeIngetrokken) {
          continue;
        }

        if (statusFilter.value === "ingetrokken" || statusFilter.value === "all") {
          statusFiltered.push(offerte);
        }
        continue;
      }

      if (statusFilter.value === "all") {
        statusFiltered.push(offerte);
        continue;
      }

      if (statusFilter.value === "nieuw") {
        if (isNieuwStatus(currentStatus as OfferteStatus)) {
          statusFiltered.push(offerte);
        }
        continue;
      }

      if (currentStatus === statusFilter.value) {
        statusFiltered.push(offerte);
      }
    }

    const anyScope = scopeFilters.value.scope8 || scopeFilters.value.scope10 || scopeFilters.value.scope12;
    const scoped: Offerte[] = [];
    for (const offerte of statusFiltered) {
      if (!anyScope) {
        scoped.push(offerte);
        continue;
      }

      if (
        (scopeFilters.value.scope8 && hasScope8(offerte)) ||
        (scopeFilters.value.scope10 && hasScope10(offerte)) ||
        (scopeFilters.value.scope12 && hasScope12(offerte))
      ) {
        scoped.push(offerte);
      }
    }

    if (!searchQuery.value.trim()) {
      return scoped;
    }

    const query = searchQuery.value.toLowerCase().trim();
    const searched: Offerte[] = [];
    for (const offerte of scoped) {
      if (
        (offerte.offerteNr && offerte.offerteNr.toLowerCase().includes(query)) ||
        (offerte.locaties?.naam && offerte.locaties.naam.toLowerCase().includes(query)) ||
        (offerte.locaties?.plaats && offerte.locaties.plaats.toLowerCase().includes(query)) ||
        (offerte.locaties?.contactNaam && offerte.locaties.contactNaam.toLowerCase().includes(query))
      ) {
        searched.push(offerte);
      }
    }

    return searched;
  });

  const displayedOffertes = computed(() => {
    return filteredOffertes.value.slice(0, displayCount.value);
  });

  const hasMore = computed(() => {
    return displayCount.value < filteredOffertes.value.length;
  });

  const loadMore = () => {
    displayCount.value = Math.min(displayCount.value + rows.value, filteredOffertes.value.length);
  };

  watch(filteredOffertes, (value) => {
    displayCount.value = Math.min(rows.value, value.length);
  });

  onMounted(async () => {
    const tabParam = route.query.tab;
    if (tabParam === "0" || tabParam === "1") {
      activeTab.value = tabParam;
    }
    await offerteStore.fetchOffertes();
  });
</script>

<template>
  <div class="offertes-page">
    <AppHeader
      :show-back-button="true"
      back-to="/"
      back-text="Terug naar dashboard"
      title="Alle offertes"
    >
    </AppHeader>

    <Tabs
      v-model:value="activeTab"
      class="offertes-tabs"
    >
      <TabList>
        <Tab value="0">Offertes</Tab>
        <Tab value="1">Bundels</Tab>
      </TabList>
      <TabPanels>
        <TabPanel
          value="0"
          class="offertes-panel"
        >
          <!-- Filters and Search Panel -->
          <Panel
            class="filters-card"
            header="Zoeken en Filteren"
          >
            <div class="filters-stack">
              <!-- Search -->
              <div class="filters-search">
                <IconField>
                  <InputIcon>
                    <i class="pi pi-search" />
                  </InputIcon>
                  <InputText
                    v-model="searchQuery"
                    placeholder="Zoek op offerte nummer, locatie, contact..."
                    class="filters-input"
                  />
                </IconField>
                <div
                  v-if="searchQuery && filteredOffertes.length !== undefined"
                  class="filters-meta"
                >
                  {{ filteredOffertes.length }} resultaten gevonden
                </div>
              </div>

              <!-- Filters Row -->
              <div class="filters-row">
                <!-- Status Filter -->
                <div class="filters-select">
                  <Select
                    v-model="statusFilter"
                    :options="statusOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Filter op status"
                    class="w-full"
                  />
                </div>

                <!-- Scope Filters -->
                <div class="filters-scopes">
                  <ToggleButton
                    v-model="scopeFilters.scope8"
                    onLabel="Scope 8"
                    offLabel="Scope 8"
                    onIcon="pi pi-check"
                    offIcon="pi pi-times"
                    class="scope-toggle"
                  />
                  <ToggleButton
                    v-model="scopeFilters.scope10"
                    onLabel="Scope 10"
                    offLabel="Scope 10"
                    onIcon="pi pi-check"
                    offIcon="pi pi-times"
                    class="scope-toggle"
                  />
                  <ToggleButton
                    v-model="scopeFilters.scope12"
                    onLabel="Scope 12"
                    offLabel="Scope 12"
                    onIcon="pi pi-check"
                    offIcon="pi pi-times"
                    class="scope-toggle"
                  />
                </div>

                <div class="filters-checkbox">
                  <Checkbox
                    v-model="showIngetrokken"
                    inputId="showIngetrokken"
                    binary
                  />
                  <label
                    for="showIngetrokken"
                    class="text-sm text-gray-700"
                    >Ingetrokken tonen</label
                  >
                </div>
              </div>
            </div>
          </Panel>

          <!-- Loading and Error States -->
          <div>
            <div
              v-if="offerteStore.loading"
              class="status-card status-loading"
            >
              <i class="pi pi-spin pi-spinner text-4xl"></i>
            </div>
            <div
              v-else-if="offerteStore.error"
              class="status-card status-error"
            >
              {{ offerteStore.error }}
            </div>
            <div
              v-else-if="filteredOffertes.length === 0"
              class="status-card status-empty"
            >
              <template v-if="searchQuery"> Geen offertes gevonden voor deze zoekopdracht. </template>
              <template v-else> Er zijn nog geen offertes beschikbaar. </template>
            </div>
            <!-- DataView of Offertes -->
            <div v-else>
              <DataView
                :value="displayedOffertes"
                layout="list"
                dataKey="id"
                class="offertes-dataview"
              >
                <template #header>
                  <div class="offertes-toolbar">
                    <span class="offertes-count">{{ filteredOffertes.length }} offertes gevonden</span>
                    <div class="offertes-toolbar-actions">
                      <Button
                        icon="pi pi-refresh"
                        @click="offerteStore.fetchOffertes()"
                        :loading="offerteStore.loading"
                        severity="secondary"
                        outlined
                        v-tooltip="'Vernieuwen'"
                        size="small"
                      />
                    </div>
                  </div>
                </template>

                <template #list="slotProps">
                  <div class="offerte-list space-y-2">
                    <div
                      v-for="offerte in toOfferteItems(slotProps.items)"
                      :key="offerte.id"
                      class="offerte-card"
                    >
                      <div
                        class="offerte-row"
                        :class="getCurrentStatus(offerte) === 'ingetrokken' ? 'is-disabled' : 'is-active'"
                        tabindex="0"
                        role="button"
                        :aria-expanded="expandedOffertes.has(offerte.id)"
                        @click="toggleExpanded(offerte.id)"
                        @keydown.enter="toggleExpanded(offerte.id)"
                        @keydown.space.prevent="toggleExpanded(offerte.id)"
                      >
                        <i
                          class="pi pi-chevron-right shrink-0 text-sm text-gray-500 transition-transform duration-200"
                          :class="expandedOffertes.has(offerte.id) ? 'rotate-90' : ''"
                        />

                        <div class="foto-boven-thumb">
                          <img
                            v-if="offerte.locaties?.fotoBoven_foto?.[0]?.remote_url"
                            :src="offerte.locaties.fotoBoven_foto[0].remote_url"
                            :alt="offerte.locaties?.naam || 'Locatie foto'"
                            class="foto-boven-img"
                            loading="lazy"
                          />
                          <i
                            v-else
                            class="pi pi-image foto-boven-placeholder-icon"
                          />
                        </div>

                        <div class="offerte-row-main">
                          <div class="offerte-row-title">
                            <span class="offerte-title">{{ offerte.offerteNr || "Offerte" }}</span>
                            <span
                              v-if="offerte.bundel_id"
                              class="bundel-indicator"
                            >
                              <i class="pi pi-link" />
                              Bundel
                            </span>
                            <span class="offerte-subtitle">{{ offerte.locaties?.naam || "Onbekende locatie" }}</span>
                          </div>
                          <div class="offerte-contact">
                            <img
                              v-if="offerte.ibedrijf?.logo"
                              :src="resolveStorageUrl(offerte.ibedrijf.logo)"
                              :alt="`Logo ${offerte.ibedrijf?.contactnaam || offerte.ibedrijf?.users?.display_name || 'bedrijf'}`"
                              class="ibedrijf-logo"
                              loading="lazy"
                              @error="handleLogoError"
                            />
                            <span class="ibedrijf-name">
                              {{ offerte.ibedrijf?.users?.display_name || offerte.ibedrijf?.contactnaam || "Onbekend" }}
                            </span>
                          </div>
                        </div>

                        <div
                          class="offerte-row-status"
                          @click.stop
                        >
                          <CustomTag
                            :color="getOfferteStatusInfo(offerte).color"
                            :label="getStatusLabel(getOfferteStatusInfo(offerte).status)"
                            clickable
                            @click.stop="showStatusHistory($event, offerte)"
                          />
                        </div>

                        <div class="offerte-row-price">
                          <span
                            v-if="getCurrentStatus(offerte) === 'ingetrokken'"
                            class="price-placeholder"
                          />
                          <span
                            v-else
                            class="price-value"
                            >{{ formatCurrency(offerte.totaalIncKorting || 0) }}</span
                          >
                        </div>
                      </div>

                      <Transition name="expand">
                        <div
                          v-if="expandedOffertes.has(offerte.id)"
                          class="offerte-details"
                        >
                          <div class="offerte-details-grid">
                            <div class="offerte-details-main">
                              <div class="offerte-detail-line">
                                <span class="offerte-detail-label">Adres</span>
                                <span class="offerte-detail-value">
                                  {{ offerte.locaties?.straat }} {{ offerte.locaties?.huisnr }},
                                  {{ offerte.locaties?.postcode }} {{ offerte.locaties?.plaats }}
                                </span>
                              </div>
                              <div
                                v-if="offerte.locaties?.bouwJaar"
                                class="offerte-detail-line"
                              >
                                <span class="offerte-detail-label">Bouwjaar</span>
                                <span class="offerte-detail-value">{{ offerte.locaties?.bouwJaar }}</span>
                              </div>
                              <div
                                v-if="offerte.locaties?.branche"
                                class="offerte-detail-line"
                              >
                                <span class="offerte-detail-label">Branche</span>
                                <span class="offerte-detail-value">{{ offerte.locaties?.branche }}</span>
                              </div>
                            </div>
                            <div class="offerte-details-aside">
                              <div class="offerte-tags">
                                <CustomTag
                                  v-if="hasScope8(offerte)"
                                  color="scope8pi"
                                  label="Scope 8"
                                />
                                <CustomTag
                                  v-if="hasScope10(offerte)"
                                  color="scope10"
                                  label="Scope 10"
                                />
                                <CustomTag
                                  v-if="hasScope12(offerte)"
                                  color="scope12pi"
                                  label="Scope 12"
                                />
                                <CustomTag
                                  v-if="hasNen1010(offerte)"
                                  color="nen1010"
                                  label="NEN 1010"
                                />
                                <CustomTag
                                  v-if="hasNen3140(offerte)"
                                  color="nen3140"
                                  label="NEN 3140"
                                />
                              </div>
                              <Button
                                v-if="getCurrentStatus(offerte) !== 'ingetrokken'"
                                label="Bekijk offerte"
                                icon="pi pi-arrow-right"
                                iconPos="right"
                                size="small"
                                @click="router.push({ name: 'OfferteDetails', params: { id: offerte.id } })"
                              />
                              <Button
                                v-if="getCurrentStatus(offerte) !== 'ingetrokken' && offerte.bundel_id"
                                label="Bekijk bundel"
                                icon="pi pi-arrow-right"
                                iconPos="right"
                                size="small"
                                @click="router.push({ name: 'BundelDetails', params: { id: offerte.bundel_id } })"
                                severity="help"
                              />
                            </div>
                          </div>
                        </div>
                      </Transition>
                    </div>
                  </div>
                </template>
              </DataView>

              <div
                v-if="hasMore"
                class="offertes-load-more"
                @click="loadMore"
              >
                <span class="offertes-load-more-text"> <i class="pi pi-arrow-down" /> Meer laden... </span>
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel
          value="1"
          class="offertes-panel"
        >
          <div class="bundel-panel">
            <BundelOverview
              v-if="authStore.user?.id"
              :opdrachtgever-id="authStore.user.id"
              @bundel-signed="() => offerteStore.fetchOffertes()"
            />
            <div
              v-else
              class="status-card status-empty"
            >
              Laden...
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <!-- Status History Popover -->
    <Popover
      ref="statusPopover"
      class="status-popover z-1000"
    >
      <div
        v-if="hoveredOfferteId"
        class="status-popover-content"
      >
        <h4 class="status-popover-title">Status Geschiedenis</h4>
        <div
          v-for="(statusEntry, index) in getStatusHistory(hoveredOfferte)"
          :key="index"
          class="status-history-row"
        >
          <div class="status-history-info">
            <span class="status-history-label">
              {{ getStatusLabel(statusEntry.status) }}
            </span>
            <span class="status-history-user">
              {{ statusEntry.door || "Systeem" }}
            </span>
          </div>
          <div class="status-history-date">
            {{ formatDate(statusEntry.datum) }}
          </div>
        </div>
        <div
          v-if="getStatusHistory(hoveredOfferte).length === 0"
          class="status-history-empty"
        >
          Geen status geschiedenis beschikbaar
        </div>
      </div>
    </Popover>

    <!-- Image Modal -->
    <div
      v-if="selectedImage"
      class="image-modal"
      @click="closeImageModal"
    >
      <div class="image-modal-frame">
        <img
          :src="selectedImage"
          alt="Expanded locatie foto"
          class="image-modal-image"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
  .offertes-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding-bottom: 2rem;
  }

  .offertes-tabs {
    background: transparent;
    border: none;
    box-shadow: none;
  }

  :deep(.p-tabpanels) {
    background: transparent;
  }

  :deep(.p-dataview-content) {
    background: transparent;
  }

  .offertes-tabs :deep(.p-tablist) {
    background: transparent;
    border-bottom: 1px solid var(--p-background-200);
    padding: 0;
    gap: 0.5rem;
  }

  .offertes-tabs :deep(.p-tab) {
    border-radius: 0;
    font-weight: 600;
    color: var(--p-background-700);
    padding: 0.75rem 1rem;
  }

  .offertes-tabs :deep(.p-tab.p-highlight) {
    color: var(--p-background-900);
    background: transparent;
    border-bottom: 2px solid var(--p-primary-500);
  }

  .offertes-panel {
    padding: 1.5rem 0;
  }

  .filters-card {
    background: transparent;
    border-radius: 0;
    border: none;
    box-shadow: none;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--p-background-200);
  }

  .filters-card :deep(.p-panel-header) {
    background: transparent;
    border-bottom: none;
    font-weight: 600;
    color: var(--p-background-800);
    padding: 0 0 0.75rem;
  }

  .filters-card :deep(.p-panel-content) {
    background: transparent;
    padding: 0;
  }

  .filters-card :deep(.p-panel-toggleable) {
    padding: 0;
  }

  .filters-stack {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .filters-search {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .filters-input {
    width: 100%;
  }

  .filters-meta {
    font-size: 0.85rem;
    color: var(--p-background-600);
  }

  .filters-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
    padding-top: 0.75rem;
    border-top: 1px solid var(--p-background-200);
  }

  .filters-select {
    flex: 1;
    min-width: 220px;
  }

  .filters-scopes {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .scope-toggle {
    min-width: 4.75rem;
    font-size: 0.85rem;
    border-radius: var(--radius-sm);
  }

  .filters-checkbox {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: var(--p-background-700);
  }

  .filters-refresh {
    margin-left: auto;
  }

  .status-card {
    border-radius: var(--radius-sm);
    border: 1px solid var(--p-background-200);
    background: var(--p-background-0);
    padding: 1.25rem;
    text-align: center;
    color: var(--p-background-600);
  }

  .status-loading {
    background: var(--p-background-0);
  }

  .status-error {
    border-color: var(--p-red-200);
    background: var(--p-red-50);
    color: var(--p-red-600);
  }

  .offertes-dataview {
    background: var(--p-background-0);
    border-radius: 0;
    border: none;
    box-shadow: none;
  }

  .offertes-dataview :deep(.p-dataview-header) {
    background: transparent;
    border-bottom: 1px solid var(--p-background-200);
    padding: 0 0 0.75rem;
  }

  .offertes-dataview :deep(.p-dataview-content) {
    padding: 0;
  }

  .offertes-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem 0;
    background-color: var(--p-background-0);
  }

  .offertes-count {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--p-background-600);
  }

  .offertes-toolbar-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .offerte-list {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding-top: 0.5rem;
    border-top: 1px solid var(--p-background-200);
  }

  .offerte-card {
    padding: 1rem 1rem;
    border-radius: 8px;
    background: var(--p-background-100);
    box-shadow: none;
    transition: border-color 0.2s ease;
  }

  .offerte-row {
    display: grid;
    grid-template-columns: auto auto minmax(0, 1fr) auto auto;
    gap: 1rem;
    align-items: center;
    cursor: pointer;
  }

  .foto-boven-thumb {
    width: 3rem;
    height: 3rem;
    border-radius: var(--radius-sm);
    border: 1px solid var(--p-background-200);
    background: var(--p-background-100);
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .foto-boven-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .foto-boven-placeholder-icon {
    font-size: 1.1rem;
    color: var(--p-background-400);
  }

  .offerte-row:focus-visible {
    outline: 2px solid var(--p-primary-200);
    outline-offset: 3px;
  }

  .offerte-row.is-disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .offerte-row.is-active:hover {
    border-color: var(--p-primary-200);
  }

  .offerte-row-main {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  .offerte-row-title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .bundel-indicator {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--p-blue-700);
    background: var(--p-blue-50);
    border: 1px solid var(--p-blue-200);
    border-radius: 0.25rem;
    padding: 0.1rem 0.4rem;
    white-space: nowrap;
  }
  .offerte-contact {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    min-width: 0;
  }

  .ibedrijf-logo {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 0.35rem;
    border: 1px solid var(--p-background-300);
    object-fit: cover;
    flex-shrink: 0;
    background: var(--p-background-0);
  }

  .ibedrijf-name {
    font-size: 0.9rem;
    color: var(--p-background-700);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .offerte-subtitle {
    font-size: 0.8rem;
    color: var(--p-background-600);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .offerte-row-status,
  .offerte-row-price {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .offerte-details {
    padding: 0.75rem 0 0.5rem;
  }

  .offerte-details-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 1rem;
  }

  .offerte-details-main {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .offerte-details-aside {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.75rem;
  }

  .offerte-detail-line {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .offerte-detail-label {
    font-size: 0.75rem;
    color: var(--p-background-500);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .offerte-detail-value {
    font-size: 0.9rem;
    color: var(--p-background-700);
  }

  .offerte-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  .price-placeholder {
    width: 5.5rem;
    height: 2.25rem;
    border-radius: var(--radius-sm);
    background: var(--p-background-300);
    display: inline-block;
  }

  .price-value {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--p-background-900);
  }

  .price-discount {
    font-size: 0.8rem;
    color: var(--p-green-600);
  }

  .expand-enter-active,
  .expand-leave-active {
    transition: all 0.2s ease-out;
    overflow: hidden;
  }

  .expand-enter-from,
  .expand-leave-to {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }

  .expand-enter-to,
  .expand-leave-from {
    opacity: 1;
    max-height: 300px;
  }

  .offertes-load-more {
    margin-top: 1rem;
    padding: 0.75rem 0;
    border-top: 1px solid var(--p-background-200);
    text-align: center;
    cursor: pointer;
    color: var(--p-background-600);
  }

  .offertes-load-more-text {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
  }

  .bundel-panel {
    background: var(--p-background-0);
    border-radius: var(--radius-md);
    border: 1px solid var(--p-background-200);
    padding: 1.5rem;
    box-shadow: none;
  }

  .status-popover-content {
    padding: 1rem;
    min-width: 18rem;
    max-width: 22rem;
  }

  .status-popover-title {
    margin: 0 0 0.6rem;
    font-size: 1rem;
    font-weight: 600;
    color: var(--p-background-900);
  }

  .status-history-row {
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--p-background-200);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .status-history-row:last-child {
    border-bottom: none;
  }

  .status-history-info {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .status-history-label {
    font-weight: 600;
    color: var(--p-background-800);
  }

  .status-history-user {
    font-size: 0.85rem;
    color: var(--p-background-600);
  }

  .status-history-date {
    font-size: 0.85rem;
    color: var(--p-background-600);
    text-align: right;
  }

  .status-history-empty {
    color: var(--p-background-600);
    padding: 0.75rem 0;
    text-align: center;
  }

  .image-modal {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .image-modal-frame {
    max-width: 90%;
    max-height: 90vh;
  }

  .image-modal-image {
    width: 100%;
    max-height: 90vh;
    object-fit: contain;
    display: block;
  }

  /* PrimeVue DataView animations using CSS animations (not replaceable with Tailwind) */
  .p-dataview .p-dataview-content {
    animation: fadeIn 0.3s ease-in-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Mobile optimizations for PrimeVue components (not replaceable with Tailwind) */
  @media (max-width: 640px) {
    .offertes-panel {
      padding: 1rem 0;
    }

    .filters-card :deep(.p-panel-header),
    .filters-card :deep(.p-panel-content) {
      padding: 0 0 0.75rem;
    }

    .offertes-dataview :deep(.p-dataview-header),
    .offertes-dataview :deep(.p-dataview-content) {
      padding-left: 0;
      padding-right: 0;
    }

    .filters-refresh {
      margin-left: 0;
    }

    .offerte-row {
      grid-template-columns: auto auto minmax(0, 1fr);
      row-gap: 0.5rem;
    }

    .foto-boven-thumb {
      width: 2.5rem;
      height: 2.5rem;
    }

    .offerte-row-status,
    .offerte-row-price {
      justify-content: flex-start;
    }

    .offerte-details-grid {
      grid-template-columns: 1fr;
    }

    .offerte-details-aside {
      align-items: flex-start;
    }
  }
</style>
